import api, { publicApi } from './api';
import { 
  normalizeTrilha, 
  normalizeUserAdmin, 
  normalizeCursoAdmin, 
  normalizeTrilhaAdmin, 
  normalizeEmpresaAdmin,
  normalizeGrupoAdmin,
  normalizeGrupoTrilha,
  normalizeGrupoUsuario,
  normalizeLessonAdmin,
} from '../utils/userAdapter';
import type {
  Curso,
  Trilha,
  Aula,
  ProgressoResumo,
  Documento,
  Favorito,
  Anotacao,
  LibraryDocumentsResponse,
  CreateUserDTO,
  UpdateUserDTO,
  CreateTrilhaDTO,
  UpdateTrilhaDTO,
  CreateCursoDTO,
  UpdateCursoDTO,
  CreateEmpresaDTO,
  UpdateEmpresaDTO,
  CreateGrupoDTO,
  UpdateGrupoDTO,
  AddTrilhaToGrupoDTO,
  Lesson,
  CreateLessonDTO,
  UpdateLessonDTO,
  CreateYouTubeLessonDTO,
  DashboardStats,
  CountResponse,
  CertificadoEmitido,
  ValidacaoCertificado,
  Perfil,
  Area,
  Cargo,
  Vinculo,
  TrilhaProgresso,
  RegraAtribuicao,
  AtribuicaoDireta,
  Quiz,
  IniciarTentativa,
  ResultadoTentativa,
  TentativaResumo,
  RelatorioVisaoGeral,
  TurmaApi,
  GestorColaborador,
  InstrutorResumo,
  InstrutorCurso,
  InstrutorAluno,
  AdminCertificadoLista,
  AdminAtribuicao,
  AtribuirTrilhaResultado,
  AdminRole,
  EmailTemplateAdmin,
  AuditoriaLista,
} from '../types';

// ============================================================================
// HELPERS
// ============================================================================
/**
 * Remove parâmetros vazios, undefined ou null de um objeto
 */
const cleanParams = <T extends Record<string, unknown>>(params?: T): T | undefined => {
  if (!params) return undefined;
  
  const cleaned = Object.entries(params).reduce((acc, [key, value]) => {
    // Remove se for undefined, null, ou string vazia
    if (value !== undefined && value !== null && value !== '') {
      acc[key] = value;
    }
    return acc;
  }, {} as Record<string, unknown>);
  
  // Se não sobrou nenhum parâmetro, retorna undefined
  return Object.keys(cleaned).length > 0 ? (cleaned as T) : undefined;
};

// ============================================================================
// AUTENTICAÇÃO
// ============================================================================
export const authService = {
  login: async (email: string, password: string) => {
    const { data } = await api.post('/Auth/login', { email, password });
    return data;
  },
  
  register: async (userData: CreateUserDTO) => {
    const { data } = await api.post('/Auth/register', userData);
    return data;
  },

  forgotPassword: async (email: string) => {
    const { data } = await api.post('/Auth/forgot-password', { email });
    return data as { message: string };
  },

  resetPassword: async (token: string, newPassword: string) => {
    const { data } = await api.post('/Auth/reset-password', { token, newPassword });
    return data as { message: string };
  },
  
  logout: async () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user');
  },
  
  getCurrentUser: async () => {
    const { data } = await api.get('/Users/me');
    return data;
  },
};

// ============================================================================
// CATÁLOGO
// ============================================================================
export const catalogService = {
  getCursos: async (params?: {
    search?: string;
    categoria?: string;
    page?: number;
    pageSize?: number;
  }) => {
    const cleanedParams = cleanParams(params);
    const { data } = await api.get<Curso[]>('/Cursos', { params: cleanedParams });
    return data.map((curso) => {
      const id = curso.id || (curso as { Id?: string }).Id || '';
      return {
        ...curso,
        id,
        slug: curso.slug || id,
        titulo: curso.titulo || (curso as { title?: string }).title || '',
        capaUrl: curso.capaUrl || (curso as { thumbnailUrl?: string }).thumbnailUrl,
        categoria: curso.categoria || (curso as { category?: string }).category || '',
      };
    });
  },
  
  getTrilhas: async () => {
    const { data } = await api.get<Trilha[]>('/Trilhas');
    return data.map(normalizeTrilha);
  },

  getMinhasTrilhas: async () => {
    const { data } = await api.get<Array<Record<string, unknown>>>('/me/trilhas');
    return data.map((item) => ({
      id: String(item.id ?? item.Id ?? ''),
      nome: String(item.nome ?? item.Nome ?? ''),
      descricao: (item.descricao ?? item.Descricao ?? '') as string,
      obrigatoria: Boolean(item.obrigatoria ?? item.Obrigatoria),
      grupoId: String(item.grupoId ?? item.GrupoId ?? ''),
      grupoNome: String(item.grupoNome ?? item.GrupoNome ?? ''),
      atribuidaEm: String(item.atribuidaEm ?? item.AtribuidaEm ?? ''),
      totalCursos: Number(item.totalCursos ?? item.TotalCursos ?? 0),
      cursosConcluidos: Number(item.cursosConcluidos ?? item.CursosConcluidos ?? 0),
      percentual: Number(item.percentual ?? item.Percentual ?? 0),
      concluida: Boolean(item.concluida ?? item.Concluida),
      proximoCursoId: (item.proximoCursoId ?? item.ProximoCursoId ?? null) as string | null,
      proximoCursoNome: (item.proximoCursoNome ?? item.ProximoCursoNome ?? null) as string | null,
      prazoEm: (item.prazoEm ?? item.PrazoEm ?? null) as string | null,
      atrasada: Boolean(item.atrasada ?? item.Atrasada),
      slug: String(item.slug ?? item.Slug ?? item.id ?? item.Id ?? ''),
    }));
  },

  getTrilhaProgresso: async (id: string): Promise<TrilhaProgresso> => {
    const { data } = await api.get<TrilhaProgresso>(`/Trilhas/${id}/progresso`);
    return data;
  },

  getTrilha: async (id: string) => {
    const { data } = await api.get<Trilha>(`/Trilhas/${id}`);
    return normalizeTrilha(data);
  },
  
  getCursoBySlug: async (slug: string) => {
    return catalogService.getCurso(slug);
  },
  
  getTrilhaBySlug: async (slug: string) => {
    const { data } = await api.get<Trilha>(`/Trilhas/slug/${slug}`);
    return normalizeTrilha(data);
  },

  getCurso: async (id: string) => {
    const { data } = await api.get<any>(`/Cursos/${id}`);
    
    // Buscar lessons separadamente (endpoint requer autenticação)
    let lessons: any[] = [];
    try {
      const lessonsResponse = await api.get<any[]>(`/Lessons/course/${id}`);
      lessons = lessonsResponse.data || [];
    } catch (error) {
      console.warn('Não foi possível carregar as lessons (pode exigir autenticação):', error);
    }
    
    // Adaptar resposta do backend (inglês) para frontend (português)
    return {
      id: data.id,
      slug: data.slug || data.id, // Se não tiver slug, usa o ID
      titulo: data.title || data.titulo,
      descricao: data.description || data.descricao,
      capa: data.coverImage || data.capa,
      nivel: data.level || data.nivel,
      categoria: data.category || data.categoria,
      cargaHorariaMin: data.durationMinutes || data.cargaHorariaMin || 0,
      instrutorId: data.instructorId || data.instrutorId,
      instrutor: data.instructorName || data.instrutor,
      preco: data.price || data.preco || 0,
      dataPublicacao: data.createdAt || data.dataPublicacao,
      ativo: data.isActive ?? data.ativo ?? true,
      
      // Converter lessons para estrutura de módulo único
      modulos: lessons.length > 0 ? [{
        id: 'default-module',
        cursoId: data.id,
        titulo: 'Aulas',
        ordem: 1,
        aulas: lessons.map((lesson: any) => ({
          id: lesson.id,
          moduloId: 'default-module',
          titulo: lesson.title || lesson.titulo,
          descricao: lesson.description || lesson.descricao,
          ordem: lesson.order || lesson.ordem || 0,
          tipo: 'video' as const,
          duracaoSeg: lesson.durationSeconds ?? (lesson.durationMinutes || 0) * 60,
          videoUrl: lesson.youtubeUrl || lesson.video?.qualities?.find((q: { fileUrl?: string }) => q.fileUrl)?.fileUrl || lesson.videoUrl,
          progresso: lesson.progress ? {
            status: lesson.isCompleted ? 'concluida' as const : 'em-andamento' as const,
            percentual: lesson.progress || 0
          } : undefined
        }))
      }] : [],
    } as Curso;
  },

  getModulo: async (id: string) => {
    const { data } = await api.get(`/Modulos/${id}`);
    return data;
  },
};

// ============================================================================
// AULAS
// ============================================================================
export const aulaService = {
  getAula: async (id: string) => {
    const { data } = await api.get<{
      id: string;
      title?: string;
      description?: string;
      order?: number;
      durationSeconds?: number;
      courseId?: string;
      youtubeUrl?: string;
      video?: { originalFileUrl?: string; qualities?: { fileUrl?: string }[] };
    }>(`/Lessons/${id}`);

    const videoUrl =
      data.youtubeUrl ||
      data.video?.originalFileUrl ||
      data.video?.qualities?.find((q) => q.fileUrl)?.fileUrl;

    return {
      id: data.id,
      moduloId: data.courseId || '',
      cursoId: data.courseId || '',
      titulo: data.title || '',
      descricao: data.description,
      ordem: data.order ?? 0,
      duracaoSeg: data.durationSeconds ?? 0,
      tipo: 'video' as const,
      videoUrl,
      midias: videoUrl ? [{ tipo: 'video', url: videoUrl }] : [],
    } as Aula & { cursoId: string };
  },
  
  startAula: async (aulaId: string) => {
    const { data } = await api.post(`/Progress/lessons/${aulaId}/start`);
    return data;
  },
  
  updatePosition: async (aulaId: string, posicao: number) => {
    const { data } = await api.post(`/Progress/lessons/${aulaId}/position`, { posicao });
    return data;
  },
  
  completeAula: async (aulaId: string, posicao?: number) => {
    const { data } = await api.post(`/Progress/aula/${aulaId}`, {
      tempoAssistido: Math.max(0, Math.round(posicao ?? 0)),
      concluida: true,
    });
    return data;
  },
};

// ============================================================================
// PROGRESSO
// ============================================================================
export const progressoService = {
  getResumo: async () => {
    const { data } = await api.get<ProgressoResumo>('/Progress/summary');
    return data;
  },
};

// ============================================================================
// QUIZZES
// ============================================================================
export const quizService = {
  getDoCurso: async (cursoId: string) => {
    const { data } = await api.get<Quiz>(`/quizzes/curso/${cursoId}`);
    return data;
  },

  iniciar: async (cursoId: string) => {
    const { data } = await api.post<IniciarTentativa>(`/quizzes/curso/${cursoId}/tentativas`);
    return data;
  },

  submeter: async (tentativaId: string, respostas: Array<{ questaoId: string; opcoesIds: string[] }>) => {
    const { data } = await api.post<ResultadoTentativa>(`/quizzes/tentativas/${tentativaId}/submeter`, { respostas });
    return data;
  },

  minhasTentativas: async (cursoId: string) => {
    const { data } = await api.get<TentativaResumo[]>(`/quizzes/curso/${cursoId}/tentativas`);
    return data;
  },
};

export const adminQuizService = {
  get: async (cursoId: string) => {
    const { data } = await api.get<Quiz>(`/quizzes/curso/${cursoId}/admin`);
    return data;
  },

  salvar: async (cursoId: string, quiz: Quiz) => {
    const { data } = await api.put<Quiz>(`/quizzes/curso/${cursoId}`, quiz);
    return data;
  },

  remover: async (cursoId: string) => {
    await api.delete(`/quizzes/curso/${cursoId}`);
  },
};

// ============================================================================
// CERTIFICADOS
// ============================================================================
export const certificadoService = {
  getMeusCertificados: async (): Promise<CertificadoEmitido[]> => {
    const { data } = await api.get<CertificadoEmitido[]>('/certificados');
    return data;
  },

  validarCertificado: async (codigo: string): Promise<ValidacaoCertificado> => {
    const { data } = await publicApi.get<ValidacaoCertificado>(`/public/certificados/${encodeURIComponent(codigo)}`);
    return data;
  },

  baixarPdf: async (id: string, codigo: string): Promise<void> => {
    const response = await api.get(`/certificados/${id}/pdf`, { responseType: 'blob' });
    const blobUrl = URL.createObjectURL(response.data as Blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = `certificado-${codigo}.pdf`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(blobUrl);
  },

  revogar: async (id: string, motivo: string): Promise<void> => {
    await api.post(`/certificados/${id}/revogar`, { motivo });
  },
};

// ============================================================================
// PERFIL
// ============================================================================
export const perfilService = {
  getMeuPerfil: async (): Promise<Perfil> => {
    const { data } = await api.get<Perfil>('/me/perfil');
    return data;
  },

  atualizar: async (payload: { nome?: string; telefone?: string; avatarUrl?: string }): Promise<Perfil> => {
    const { data } = await api.patch<Perfil>('/me/perfil', payload);
    return data;
  },

  alterarSenha: async (senhaAtual: string, novaSenha: string): Promise<void> => {
    await api.post('/me/perfil/senha', { senhaAtual, novaSenha });
  },
};

// ============================================================================
// ESTRUTURA ORGANIZACIONAL
// ============================================================================
export const areaService = {
  listar: async (empresaId?: string): Promise<Area[]> => {
    const { data } = await api.get<Area[]>('/areas', { params: cleanParams({ empresaId }) });
    return data;
  },

  criar: async (payload: {
    empresaId: string;
    parentId?: string | null;
    nome: string;
    codigo?: string;
    ordem?: number;
  }): Promise<{ id: string }> => {
    const { data } = await api.post<{ id: string }>('/areas', payload);
    return data;
  },

  atualizar: async (id: string, payload: {
    empresaId: string;
    parentId?: string | null;
    nome: string;
    codigo?: string;
    ordem?: number;
    ativo?: boolean;
  }): Promise<void> => {
    await api.put(`/areas/${id}`, payload);
  },

  remover: async (id: string): Promise<void> => {
    await api.delete(`/areas/${id}`);
  },
};

export const cargoService = {
  listar: async (empresaId?: string): Promise<Cargo[]> => {
    const { data } = await api.get<Cargo[]>('/cargos', { params: cleanParams({ empresaId }) });
    return data;
  },

  criar: async (payload: {
    empresaId?: string | null;
    nome: string;
    codigo?: string;
    nivel?: string;
    descricao?: string;
  }): Promise<{ id: string }> => {
    const { data } = await api.post<{ id: string }>('/cargos', payload);
    return data;
  },

  atualizar: async (id: string, payload: {
    empresaId?: string | null;
    nome: string;
    codigo?: string;
    nivel?: string;
    descricao?: string;
    ativo?: boolean;
  }): Promise<void> => {
    await api.put(`/cargos/${id}`, payload);
  },

  remover: async (id: string): Promise<void> => {
    await api.delete(`/cargos/${id}`);
  },
};

export const vinculoService = {
  listar: async (params?: { empresaId?: string; usuarioId?: string; areaId?: string }): Promise<Vinculo[]> => {
    const { data } = await api.get<Vinculo[]>('/vinculos', { params: cleanParams(params) });
    return data;
  },

  salvar: async (payload: {
    usuarioId: string;
    empresaId: string;
    areaId?: string | null;
    cargoId?: string | null;
    gestorId?: string | null;
    inicio?: string;
    matricula?: string;
  }): Promise<void> => {
    await api.post('/vinculos', payload);
  },

  encerrar: async (id: string): Promise<void> => {
    await api.delete(`/vinculos/${id}`);
  },
};

// ============================================================================
// REGRAS DE ATRIBUIÇÃO
// ============================================================================
export const regraAtribuicaoService = {
  listar: async (empresaId?: string): Promise<RegraAtribuicao[]> => {
    const { data } = await api.get<RegraAtribuicao[]>('/regras-atribuicao', {
      params: cleanParams({ empresaId }),
    });
    return data;
  },

  criar: async (payload: {
    empresaId: string;
    trilhaId: string;
    areaId?: string | null;
    cargoId?: string | null;
    obrigatoria?: boolean;
    prazoDias?: number | null;
  }): Promise<{ id: string }> => {
    const { data } = await api.post<{ id: string }>('/regras-atribuicao', payload);
    return data;
  },

  remover: async (id: string): Promise<void> => {
    await api.delete(`/regras-atribuicao/${id}`);
  },
};

export const atribuicaoService = {
  getMinhas: async (): Promise<AtribuicaoDireta[]> => {
    const { data } = await api.get<AtribuicaoDireta[]>('/me/atribuicoes');
    return data;
  },
};

// ============================================================================
// TURMAS
// ============================================================================
export const turmaService = {
  getTodas: async (): Promise<TurmaApi[]> => {
    const { data } = await api.get<TurmaApi[]>('/turmas');
    return data;
  },

  getMinhas: async (): Promise<TurmaApi[]> => {
    const { data } = await api.get<TurmaApi[]>('/turmas/minhas');
    return data;
  },

  getMinhasTurmas: async (): Promise<TurmaApi[]> => {
    const { data } = await api.get<TurmaApi[]>('/turmas/minhas');
    return data;
  },

  getTurma: async (id: string): Promise<TurmaApi> => {
    const { data } = await api.get<TurmaApi>(`/turmas/${id}`);
    return data;
  },

  criar: async (payload: {
    nome: string;
    descricao?: string;
    instructorId?: string;
    dataInicio: string;
    dataFim?: string | null;
    ativo?: boolean;
  }): Promise<TurmaApi> => {
    const { data } = await api.post<TurmaApi>('/turmas', payload);
    return data;
  },

  atualizar: async (id: string, payload: {
    nome: string;
    descricao?: string;
    instructorId?: string;
    dataInicio: string;
    dataFim?: string | null;
    ativo?: boolean;
  }): Promise<TurmaApi> => {
    const { data } = await api.put<TurmaApi>(`/turmas/${id}`, payload);
    return data;
  },

  remover: async (id: string): Promise<void> => {
    await api.delete(`/turmas/${id}`);
  },

  matricular: async (id: string, alunoId: string): Promise<void> => {
    await api.post(`/turmas/${id}/alunos`, { alunoId });
  },

  desmatricular: async (id: string, alunoId: string): Promise<void> => {
    await api.delete(`/turmas/${id}/alunos/${alunoId}`);
  },
};

// ============================================================================
// BIBLIOTECA
// ============================================================================
export const bibliotecaService = {
  getDocumentos: async (params?: {
    search?: string;
    module?: string;
    page?: number;
  }) => {
    const cleanedParams = cleanParams(params);
    const { data } = await api.get<LibraryDocumentsResponse>('/Library/documents', { params: cleanedParams });
    return data;
  },
  
  getDocumento: async (id: string) => {
    const { data } = await api.get<Documento>(`/Library/documents/${id}`);
    return data;
  },
};

// ============================================================================
// FAVORITOS
// ============================================================================
export const favoritoService = {
  getMeusFavoritos: async () => {
    const { data } = await api.get<Favorito[]>('/Favorites');
    return data;
  },
  
  addFavorito: async (tipo: 'curso' | 'aula' | 'documento', refId: string) => {
    const { data } = await api.post('/Favorites', { tipo, refId });
    return data;
  },
  
  removeFavorito: async (tipo: string, refId: string) => {
    await api.delete(`/Favorites/${tipo}/${refId}`);
  },
};

// ============================================================================
// ANOTAÇÕES
// ============================================================================
export const anotacaoService = {
  getMinhasAnotacoes: async (aulaId?: string) => {
    const { data } = await api.get<Anotacao[]>('/Notes', {
      params: aulaId ? { lessonId: aulaId } : undefined,
    });
    return data;
  },
  
  createAnotacao: async (aulaId: string, texto: string, timestampSeg?: number) => {
    const { data } = await api.post<Anotacao>('/Notes', {
      lessonId: aulaId,
      content: texto,
      timestamp: timestampSeg,
    });
    return data;
  },
  
  updateAnotacao: async (id: string, texto: string) => {
    const { data} = await api.put<Anotacao>(`/Notes/${id}`, { content: texto });
    return data;
  },
  
  deleteAnotacao: async (id: string) => {
    await api.delete(`/Notes/${id}`);
  },
};

// ============================================================================
// ADMIN - USERS
// ============================================================================
export const adminUserService = {
  getUsers: async (params?: { page?: number; search?: string; role?: string }) => {
    const { data } = await api.get('/Users', { params });
    return Array.isArray(data) ? data.map(normalizeUserAdmin) : data;
  },
  
  getUser: async (id: string) => {
    const { data } = await api.get(`/Users/${id}`);
    return normalizeUserAdmin(data);
  },
  
  createUser: async (userData: CreateUserDTO) => {
    const mappedData = {
      name: userData.nome || userData.name,
      email: userData.email,
      password: userData.senha || userData.password,
      phoneNumber: userData.phoneNumber,
      roles: userData.roles || [],
      isActive: userData.ativo !== undefined ? userData.ativo : true,
    };
    const { data } = await api.post('/Users', mappedData);
    return data;
  },
  
  updateUser: async (id: string, userData: UpdateUserDTO) => {
    const mappedData: any = {
      name: userData.nome || userData.name,
      email: userData.email,
      roles: [...new Set(userData.roles || [])], // Remover duplicados
      isActive: userData.ativo ?? userData.isActive ?? true,
    };
    
    // Adicionar phoneNumber apenas se não for vazio
    if (userData.phoneNumber) {
      mappedData.phoneNumber = userData.phoneNumber;
    }
    
    // Adicionar senha apenas se fornecida
    if (userData.senha || userData.password) {
      mappedData.password = userData.senha || userData.password;
    }
    
    const { data } = await api.put(`/Users/${id}`, mappedData);
    return normalizeUserAdmin(data);
  },
  
  deleteUser: async (id: string) => {
    await api.delete(`/Users/${id}`);
  },
};

// ============================================================================
// ADMIN - TRILHAS
// ============================================================================
export const adminTrilhaService = {
  getTrilhas: async (params?: { page?: number; search?: string; ativa?: boolean }) => {
    const { data } = await api.get('/Trilhas', { params });
    return Array.isArray(data) ? data.map(normalizeTrilhaAdmin) : data;
  },
  
  getTrilha: async (id: string) => {
    const { data } = await api.get(`/Trilhas/${id}`);
    return normalizeTrilhaAdmin(data);
  },
  
  createTrilha: async (trilhaData: CreateTrilhaDTO) => {
    const { data } = await api.post('/Trilhas', trilhaData);
    return data;
  },
  
  updateTrilha: async (id: string, trilhaData: UpdateTrilhaDTO) => {
    const { data } = await api.put(`/Trilhas/${id}`, trilhaData);
    return data;
  },
  
  deleteTrilha: async (id: string) => {
    await api.delete(`/Trilhas/${id}`);
  },
};

/**
 * Normaliza o payload de curso para o contrato da API:
 * duração numérica em minutos e instrutor apenas quando for um Guid válido.
 */
function mapCursoPayload(cursoData: CreateCursoDTO | UpdateCursoDTO) {
  const bruto = cursoData as CreateCursoDTO & {
    duracao?: string | number;
    duracaoMinutos?: number;
    instrutorId?: string;
    certificadoHabilitado?: boolean;
    validadeCertificadoMeses?: number;
  };

  const duracao = Number(bruto.duracaoMinutos ?? bruto.duracao);
  const instrutorId = bruto.instrutorId && /^[0-9a-f-]{36}$/i.test(bruto.instrutorId)
    ? bruto.instrutorId
    : undefined;

  return {
    titulo: bruto.titulo,
    descricao: bruto.descricao,
    categoria: bruto.categoria,
    nivel: bruto.nivel,
    capaUrl: bruto.capaUrl || undefined,
    ativo: bruto.ativo,
    duracaoMinutos: Number.isFinite(duracao) && duracao > 0 ? Math.round(duracao) : undefined,
    instrutorId,
    certificadoHabilitado: bruto.certificadoHabilitado,
    validadeCertificadoMeses: bruto.validadeCertificadoMeses,
  };
}

// ============================================================================
// ADMIN - CURSOS
// ============================================================================
export const adminCursoService = {
  getCursos: async (params?: { page?: number; search?: string; ativo?: boolean; categoria?: string }) => {
    const { data } = await api.get('/Cursos', { params });
    return Array.isArray(data) ? data.map(normalizeCursoAdmin) : data;
  },
  
  getCurso: async (id: string) => {
    const { data } = await api.get(`/Cursos/${id}`);
    return normalizeCursoAdmin(data);
  },
  
  createCurso: async (cursoData: CreateCursoDTO) => {
    const { data } = await api.post('/Cursos', mapCursoPayload(cursoData));
    return data;
  },
  
  updateCurso: async (id: string, cursoData: UpdateCursoDTO) => {
    const { data } = await api.put(`/Cursos/${id}`, mapCursoPayload(cursoData));
    return data;
  },
  
  deleteCurso: async (id: string) => {
    await api.delete(`/Cursos/${id}`);
  },
};

// ============================================================================
// ADMIN - EMPRESAS
// ============================================================================
export const adminEmpresaService = {
  getEmpresas: async (params?: { page?: number; search?: string; ativo?: boolean }) => {
    console.log('🔍 [AdminEmpresaService] Buscando empresas com params:', params);
    const { data } = await api.get('/Empresas', { params });
    console.log('📦 [AdminEmpresaService] Dados brutos recebidos da API:', data);
    console.log('📊 [AdminEmpresaService] Tipo de dados:', Array.isArray(data) ? 'Array' : typeof data);
    
    const normalized = Array.isArray(data) ? data.map(normalizeEmpresaAdmin) : data;
    console.log('✨ [AdminEmpresaService] Dados normalizados:', normalized);
    
    return normalized;
  },
  
  getEmpresa: async (id: string) => {
    const { data } = await api.get(`/Empresas/${id}`);
    return normalizeEmpresaAdmin(data);
  },
  
  createEmpresa: async (empresaData: CreateEmpresaDTO) => {
    console.log('➕ [AdminEmpresaService] Criando nova empresa');
    console.log('📦 [AdminEmpresaService] Payload:', JSON.stringify(empresaData, null, 2));
    
    const { data } = await api.post('/Empresas', empresaData);
    
    console.log('✅ [AdminEmpresaService] Resposta do POST:', data);
    return data;
  },
  
  updateEmpresa: async (id: string, empresaData: UpdateEmpresaDTO) => {
    console.log('✏️ [AdminEmpresaService] Atualizando empresa:', { id, empresaData });
    console.log('📤 [AdminEmpresaService] Endpoint:', `PUT /Empresas/${id}`);
    console.log('📦 [AdminEmpresaService] Payload:', JSON.stringify(empresaData, null, 2));
    
    const { data } = await api.put(`/Empresas/${id}`, empresaData);
    
    console.log('✅ [AdminEmpresaService] Resposta do PUT:', data);
    return data;
  },
  
  deleteEmpresa: async (id: string) => {
    await api.delete(`/Empresas/${id}`);
  },

  publishOnboarding: async (id: string) => {
    const { data } = await api.post(`/Empresas/${id}/onboarding`);
    return {
      publicado: Boolean(data.publicado ?? data.Publicado),
      trilhaId: (data.trilhaId ?? data.TrilhaId) as string | undefined,
      cursoId: (data.cursoId ?? data.CursoId) as string | undefined,
      aulaId: (data.aulaId ?? data.AulaId) as string | undefined,
      videoPublicado: Boolean(data.videoPublicado ?? data.VideoPublicado),
      nomeTrilha: (data.nomeTrilha ?? data.NomeTrilha) as string | undefined,
    };
  },
};

// ============================================================================
// ADMIN - GRUPOS DE TRILHAS
// ============================================================================
export const adminGrupoService = {
  // Gerenciar Grupos
  getGrupos: async (empresaId: string) => {
    const { data } = await api.get('/Grupos', { params: { empresaId } });
    return Array.isArray(data) ? data.map(normalizeGrupoAdmin) : data;
  },
  
  getGrupo: async (id: string) => {
    const { data } = await api.get(`/Grupos/${id}`);
    return normalizeGrupoAdmin(data);
  },
  
  createGrupo: async (empresaId: string, grupoData: CreateGrupoDTO) => {
    console.log('📤 [AdminGrupoService] Criando grupo');
    console.log('🔑 [AdminGrupoService] EmpresaId:', empresaId);
    console.log('📦 [AdminGrupoService] Payload:', JSON.stringify(grupoData, null, 2));
    console.log('🌐 [AdminGrupoService] URL:', `/Grupos?empresaId=${empresaId}`);
    
    const { data } = await api.post('/Grupos', grupoData, { params: { empresaId } });
    
    console.log('✅ [AdminGrupoService] Resposta recebida:', data);
    return normalizeGrupoAdmin(data);
  },
  
  updateGrupo: async (id: string, grupoData: UpdateGrupoDTO) => {
    const { data } = await api.put(`/Grupos/${id}`, grupoData);
    return normalizeGrupoAdmin(data);
  },
  
  deleteGrupo: async (id: string) => {
    await api.delete(`/Grupos/${id}`);
  },

  // Gerenciar Trilhas do Grupo
  getTrilhasDoGrupo: async (grupoId: string) => {
    const { data } = await api.get(`/Grupos/${grupoId}/trilhas`);
    return Array.isArray(data) ? data.map(normalizeGrupoTrilha) : data;
  },
  
  addTrilhaAoGrupo: async (grupoId: string, trilhaData: AddTrilhaToGrupoDTO) => {
    const { data } = await api.post(`/Grupos/${grupoId}/trilhas`, trilhaData);
    return data;
  },
  
  removeTrilhaDoGrupo: async (grupoId: string, trilhaId: string) => {
    await api.delete(`/Grupos/${grupoId}/trilhas/${trilhaId}`);
  },

  // Gerenciar Usuários do Grupo
  getUsuariosDoGrupo: async (grupoId: string) => {
    const { data } = await api.get(`/Grupos/${grupoId}/usuarios`);
    return Array.isArray(data) ? data.map(normalizeGrupoUsuario) : data;
  },
  
  addUsuarioAoGrupo: async (grupoId: string, userId: string) => {
    const { data } = await api.post(`/Grupos/${grupoId}/usuarios/${userId}`);
    return data;
  },
  
  removeUsuarioDoGrupo: async (grupoId: string, userId: string) => {
    await api.delete(`/Grupos/${grupoId}/usuarios/${userId}`);
  },
};

// ============================================================================
// ADMIN - LESSONS (Painel Instrutor)
// ============================================================================
export const adminLessonService = {
  // Listar lessons (todas ou filtradas por curso)
  getLessons: async (params?: { courseId?: string; page?: number; pageSize?: number }) => {
    const cleanedParams = cleanParams(params);
    const { data } = await api.get<Lesson[]>('/Lessons', { params: cleanedParams });
    return Array.isArray(data) ? data.map(normalizeLessonAdmin) : data;
  },
  
  // Buscar lesson por ID
  getLesson: async (id: string) => {
    const { data } = await api.get<Lesson>(`/Lessons/${id}`);
    return normalizeLessonAdmin(data);
  },
  
  // Criar nova lesson (Upload)
  createLesson: async (lessonData: CreateLessonDTO) => {
    const { data } = await api.post<Lesson>('/Lessons', lessonData);
    return normalizeLessonAdmin(data);
  },
  
  // Criar nova lesson (YouTube)
  createYouTubeLesson: async (lessonData: CreateYouTubeLessonDTO) => {
    const { data } = await api.post<Lesson>('/Lessons/youtube', lessonData);
    return normalizeLessonAdmin(data);
  },
  
  // Atualizar lesson
  updateLesson: async (id: string, lessonData: UpdateLessonDTO) => {
    const { data } = await api.put<Lesson>(`/Lessons/${id}`, lessonData);
    return normalizeLessonAdmin(data);
  },
  
  // Deletar lesson
  deleteLesson: async (id: string) => {
    await api.delete(`/Lessons/${id}`);
  },
  
  // Buscar lessons de um curso específico
  getLessonsByCourse: async (courseId: string) => {
    const { data } = await api.get<Lesson[]>(`/Lessons/course/${courseId}`);
    return Array.isArray(data) ? data.map(normalizeLessonAdmin) : data;
  },
};

// ============================================================================
// STATS - ESTATÍSTICAS OTIMIZADAS
// ============================================================================
export const statsService = {
  // Endpoint agregado - Dashboard completo (1 chamada)
  getDashboardStats: async (): Promise<DashboardStats> => {
    const { data } = await api.get<DashboardStats>('/Stats/Dashboard');
    
    // Normalizar para camelCase se vier PascalCase
    return {
      totalUsers: data.TotalUsers ?? data.totalUsers ?? 0,
      totalCursos: data.TotalCursos ?? data.totalCursos ?? 0,
      totalTrilhas: data.TotalTrilhas ?? data.totalTrilhas ?? 0,
      totalEmpresas: data.TotalEmpresas ?? data.totalEmpresas ?? 0,
      totalVideos: data.TotalVideos ?? data.totalVideos ?? 0,
      totalGrupos: data.TotalGrupos ?? data.totalGrupos ?? 0,
    };
  },

  // Endpoints individuais (uso granular)
  getUsersCount: async (): Promise<number> => {
    const { data } = await api.get<CountResponse>('/Stats/Users/Count');
    return data.Count ?? data.count ?? 0;
  },

  getCursosCount: async (): Promise<number> => {
    const { data } = await api.get<CountResponse>('/Stats/Cursos/Count');
    return data.Count ?? data.count ?? 0;
  },

  getTrilhasCount: async (): Promise<number> => {
    const { data } = await api.get<CountResponse>('/Stats/Trilhas/Count');
    return data.Count ?? data.count ?? 0;
  },

  getEmpresasCount: async (): Promise<number> => {
    const { data } = await api.get<CountResponse>('/Stats/Empresas/Count');
    return data.Count ?? data.count ?? 0;
  },

  getVideosCount: async (): Promise<number> => {
    const { data } = await api.get<CountResponse>('/Stats/Videos/Count');
    return data.Count ?? data.count ?? 0;
  },

  getGruposCount: async (): Promise<number> => {
    const { data } = await api.get<CountResponse>('/Stats/Grupos/Count');
    return data.Count ?? data.count ?? 0;
  },
};

// ============================================================================
// RELATÓRIOS
// ============================================================================
export const relatorioService = {
  getVisaoGeral: async (params?: { empresaId?: string; areaId?: string; trilhaId?: string }): Promise<RelatorioVisaoGeral> => {
    const { data } = await api.get<RelatorioVisaoGeral>('/relatorios/visao-geral', {
      params: cleanParams(params),
    });
    return data;
  },

  exportarCsv: async (params?: { empresaId?: string; areaId?: string; trilhaId?: string }): Promise<void> => {
    const response = await api.get('/relatorios/exportar.csv', {
      params: cleanParams(params),
      responseType: 'blob',
    });

    const blobUrl = URL.createObjectURL(response.data as Blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = `relatorio-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(blobUrl);
  },
};

export const notificacaoService = {
  executarLembretes: async (): Promise<{ enviadas: number; executadoEm: string }> => {
    const { data } = await api.post<{ enviadas: number; executadoEm: string }>('/notificacoes/executar');
    return data;
  },
};

export const reciclagemService = {
  executar: async (): Promise<{ certificadosExpirados: number; trilhasReatribuidas: number; executadoEm: string }> => {
    const { data } = await api.post('/certificados/reciclar');
    return data;
  },
};

// ============================================================================
// PAINEL DO GESTOR
// ============================================================================
export const gestorService = {
  getEquipe: async (): Promise<GestorColaborador[]> => {
    const { data } = await api.get<GestorColaborador[]>('/gestor/equipe');
    return data;
  },

  getColaborador: async (usuarioId: string): Promise<GestorColaborador> => {
    const { data } = await api.get<GestorColaborador>(`/gestor/equipe/${usuarioId}`);
    return data;
  },

  exportarCsv: async (): Promise<void> => {
    const response = await api.get('/gestor/equipe/exportar.csv', { responseType: 'blob' });

    const blobUrl = URL.createObjectURL(response.data as Blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = `equipe-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(blobUrl);
  },
};

export const instrutorService = {
  getResumo: async (): Promise<InstrutorResumo> => {
    const { data } = await api.get<InstrutorResumo>('/instrutor/resumo');
    return data;
  },

  getCursos: async (): Promise<InstrutorCurso[]> => {
    const { data } = await api.get<InstrutorCurso[]>('/instrutor/cursos');
    return data;
  },

  getAlunos: async (cursoId: string): Promise<InstrutorAluno[]> => {
    const { data } = await api.get<InstrutorAluno[]>(`/instrutor/cursos/${cursoId}/alunos`);
    return data;
  },
};

export const adminCertificadoService = {
  listar: async (params?: {
    busca?: string;
    status?: string;
    tipo?: string;
    empresaId?: string;
    page?: number;
    pageSize?: number;
  }): Promise<AdminCertificadoLista> => {
    const { data } = await api.get<AdminCertificadoLista>('/admin/certificados', {
      params: cleanParams(params),
    });
    return data;
  },

  baixarPdf: async (id: string, codigo: string): Promise<void> => {
    const response = await api.get(`/admin/certificados/${id}/pdf`, { responseType: 'blob' });
    const blobUrl = URL.createObjectURL(response.data as Blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = `certificado-${codigo}.pdf`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(blobUrl);
  },

  revogar: async (id: string, motivo: string): Promise<void> => {
    await api.post(`/admin/certificados/${id}/revogar`, { motivo });
  },
};

export const adminAtribuicaoService = {
  listar: async (trilhaId?: string): Promise<AdminAtribuicao[]> => {
    const { data } = await api.get<AdminAtribuicao[]>('/admin/atribuicoes', {
      params: cleanParams({ trilhaId }),
    });
    return data;
  },

  atribuir: async (payload: {
    trilhaId: string;
    usuarioIds: string[];
    prazoDias?: number | null;
    obrigatoria?: boolean;
  }): Promise<AtribuirTrilhaResultado> => {
    const { data } = await api.post<AtribuirTrilhaResultado>('/admin/atribuicoes', payload);
    return data;
  },

  remover: async (id: string): Promise<void> => {
    await api.delete(`/admin/atribuicoes/${id}`);
  },
};

export const adminRoleService = {
  listar: async (): Promise<AdminRole[]> => {
    const { data } = await api.get<AdminRole[]>('/Roles');
    return data;
  },

  criar: async (payload: { name: string; description?: string; permissions: string[] }): Promise<AdminRole> => {
    const { data } = await api.post<AdminRole>('/Roles', payload);
    return data;
  },

  atualizar: async (id: string, payload: {
    name?: string;
    description?: string;
    permissions?: string[];
    isActive?: boolean;
  }): Promise<AdminRole> => {
    const { data } = await api.put<AdminRole>(`/Roles/${id}`, payload);
    return data;
  },

  remover: async (id: string): Promise<void> => {
    await api.delete(`/Roles/${id}`);
  },
};

export const adminEmailService = {
  listar: async (): Promise<EmailTemplateAdmin[]> => {
    const { data } = await api.get<EmailTemplateAdmin[]>('/admin/emails');
    return data;
  },

  atualizar: async (chave: string, payload: {
    nome: string;
    assunto: string;
    corpoHtml: string;
    isActive: boolean;
  }): Promise<EmailTemplateAdmin> => {
    const { data } = await api.put<EmailTemplateAdmin>(`/admin/emails/${chave}`, payload);
    return data;
  },
};

export const adminAuditoriaService = {
  listar: async (params?: {
    usuario?: string;
    metodo?: string;
    dataInicio?: string;
    dataFim?: string;
    page?: number;
    pageSize?: number;
  }): Promise<AuditoriaLista> => {
    const { data } = await api.get<AuditoriaLista>('/admin/auditoria', {
      params: cleanParams(params),
    });
    return data;
  },
};

